/** @jsxImportSource @emotion/react */
import { CalendarOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { DatePicker as AntdDatePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { paragraphSmall, weightRegular } from 'src/components/css'
import Text from '../Text'
import { ErrorLabel } from '../ErrorLabel'

type RangePickerProps = {
  label: string
  backgroundColor?: string
  onChange: (e: any) => void
  starts_at: Dayjs | null | undefined | string
  ends_at: Dayjs | null | undefined | string
  fieldErrors: string[]
  name: string
  customError: any
  allowClear?: boolean
  showTime?: boolean | object
  /**
   * Require an explicit OK per panel. With `showTime` antd defaults this to
   * true, which commits one end at a time and emits the untouched end as
   * `undefined` — passing false commits both ends as they are picked.
   */
  needConfirm?: boolean
  /**
   * Whether each end may be empty. Defaults to the historic behaviour: only
   * clearable pickers allow it. Set `[true, true]` alongside
   * `needConfirm={false}` so antd emits the pair as it is being built instead
   * of withholding onChange until both ends are committed — but only where the
   * caller's onChange tolerates a missing end.
   */
  allowEmpty?: [boolean, boolean]
  format?: {
    format?: string
    type?: 'mask' | 'string'
  }
}

const getDate = (
  start_at: Dayjs | null | undefined | string,
  end_at: Dayjs | null | undefined | string
): [Dayjs | null | undefined, Dayjs | null | undefined] => {
  if (!start_at && !end_at) {
    return [undefined, undefined]
  }
  if (start_at && !end_at) {
    return [dayjs(start_at), undefined]
  }
  if (!start_at && end_at) {
    return [undefined, dayjs(end_at)]
  }
  return [dayjs(start_at), dayjs(end_at)]
}

const RangePicker = ({
  label,
  backgroundColor = colors.SECONDARY_200,
  onChange,
  starts_at,
  ends_at,
  fieldErrors,
  name,
  customError,
  allowClear = false,
  showTime,
  needConfirm,
  allowEmpty,
  format = {
    format: 'MM/DD/YYYY',
    type: 'mask',
  },
}: RangePickerProps) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: flex-start;
        align-self: stretch;
        width: 100%;
        .ant-picker-input > input {
          ${paragraphSmall}
          ${weightRegular}
          text-align: center;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
          flex: 1 0 0;
          align-self: stretch;
        `}
      >
        <Text variant='paragraphSmall' weight='regular'>
          {label}
        </Text>
        <AntdDatePicker.RangePicker
          status={
            fieldErrors[name] || fieldErrors?.includes(name) ? 'error' : ''
          }
          css={css`
            ${paragraphSmall}
            ${weightRegular}
            width: 100%;
            background: ${backgroundColor};
            border: 1px solid ${colors.SECONDARY_100};
            border-radius: 8px 8px 8px 8px;
            height: 40px;
            text-align: center;
          `}
          format={
            format.format || (showTime ? 'MM/DD/YYYY hh:mm A' : 'MM/DD/YYYY')
          }
          showTime={showTime}
          needConfirm={needConfirm}
          size='large'
          allowEmpty={
            allowEmpty ?? (allowClear ? [true, true] : [false, false])
          }
          allowClear={allowClear}
          onChange={(e) => {
            if (e) {
              onChange(e)
              return
            }
            // Cleared. Callers that opted into clearing expect an empty pair to
            // destructure; the rest keep the historic null. Emitting both (as
            // this did) pushed the null through first, which threw in callers
            // that destructure it straight away.
            onChange(allowClear ? [] : null)
          }}
          suffixIcon={
            <CalendarOutlined
              css={css`
                color: ${colors.WHITE};
                font-size: 24px;
              `}
            />
          }
          value={getDate(starts_at, ends_at)}
          getPopupContainer={(triggerNode) => {
            return triggerNode.parentNode as HTMLElement
          }}
          popupStyle={{ position: 'fixed' }}
        />
        <ErrorLabel
          fieldErrors={fieldErrors || []}
          customError={customError || ''}
          name={name}
        />
      </div>
    </div>
  )
}

export default RangePicker
